import { Download, ExternalLink } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Link } from '@/components/ui/Link'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { StepStatus } from './checkoutData'
import { StepStatusBadge } from './StepStatusBadge'

// Mock: how many certificates go out, how fast, and how long the filing takes to update after payment.
const CERTIFICATE_COUNT = 6
const CERTIFICATE_INTERVAL_MS = 700
const FILING_UPDATE_MS = 3000

const LONG_DATE: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' }

function TrackerRow({
  title,
  isBold,
  status,
  action,
  children,
}: {
  title: string
  isBold?: boolean
  status: React.ReactNode
  /** Sits on the right, beside the status. */
  action?: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    // QuoteCard's Carrier details row: label left, value right, a hairline between rows.
    <li className="flex min-h-9 flex-wrap items-center justify-between gap-2 px-2 py-2 text-xs">
      <span className="flex min-w-0 flex-col items-start gap-1">
        <span className={cn('text-foreground', isBold ? 'font-bold' : 'font-medium')}>{title}</span>
        {children}
      </span>
      {/* Wraps under the label on a narrow screen rather than clipping. */}
      <span className="flex flex-wrap items-center gap-3">
        {action}
        {status}
      </span>
    </li>
  )
}

/** Mock link with nothing behind it in the prototype. A download icon leads; an external-link icon always trails. */
function DocumentLink({
  icon,
  external,
  children,
}: {
  icon: React.ReactNode
  external?: boolean
  children: React.ReactNode
}) {
  return (
    <Link
      kind="util"
      size="sm"
      emphasis
      icon={external ? undefined : icon}
      href="#"
      onClick={(event) => event.preventDefault()}
    >
      {children}
      {external && icon}
    </Link>
  )
}

function countdown(to: Date, now: number) {
  const seconds = Math.max(0, Math.floor((to.getTime() - now) / 1000))
  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds % 60)}s`
}

/** Always shown, so the customer knows what happens after payment before they pay. */
export function ActivationTracker({
  carrierName,
  isPaid,
  startDate,
  activatesInSeconds,
}: {
  carrierName: string
  isPaid: boolean
  startDate?: Date
  activatesInSeconds?: number
}) {
  const [paidAt, setPaidAt] = useState<number>()
  const [certificatesSent, setCertificatesSent] = useState(0)
  const [isFilingUpdated, setIsFilingUpdated] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const areCertificatesSent = certificatesSent === CERTIFICATE_COUNT
  const effectiveAt =
    activatesInSeconds !== undefined
      ? paidAt !== undefined && new Date(paidAt + activatesInSeconds * 1000)
      : startDate && new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  const isActive = isPaid && !!effectiveAt && now >= effectiveAt.getTime()
  const effectiveTime = effectiveAt
    ? effectiveAt
        .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        .replace(' ', '')
        .toLowerCase()
    : ''

  useEffect(() => {
    if (!isPaid) return
    setPaidAt(Date.now())
    const filing = setTimeout(() => setIsFilingUpdated(true), FILING_UPDATE_MS)
    return () => clearTimeout(filing)
  }, [isPaid])

  useEffect(() => {
    if (!isPaid || areCertificatesSent) return
    const certificates = setInterval(
      () => setCertificatesSent((sent) => Math.min(sent + 1, CERTIFICATE_COUNT)),
      CERTIFICATE_INTERVAL_MS
    )
    return () => clearInterval(certificates)
  }, [isPaid, areCertificatesSent])

  useEffect(() => {
    if (!isPaid || isActive) return
    const clock = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(clock)
  }, [isPaid, isActive])

  return (
    <div className="mt-4 flex flex-col gap-2">
      <section className="bg-ink-50 flex flex-col gap-1 rounded-xl px-2 pt-4 pb-3">
        <h3 className="px-2 text-sm font-bold">Track your coverage activation</h3>
        {/* A list, not a stepper: after payment these can all run at once. */}
        <ul className="divide-ink-100 flex flex-col divide-y">
          <TrackerRow
            title="Policy document received"
            status={<StepStatusBadge status={isPaid ? StepStatus.COMPLETE : StepStatus.PENDING} />}
            action={isPaid && <DocumentLink icon={<Download />}>Download policy documents</DocumentLink>}
          />

          <TrackerRow
            title="Certificates of insurance"
            status={
              isPaid && !areCertificatesSent ? (
                <Badge variant="secondary" className="shrink whitespace-normal">
                  <Spinner className="size-3" />
                  Creating and sending certificates… {certificatesSent}/{CERTIFICATE_COUNT}
                </Badge>
              ) : (
                <StepStatusBadge status={areCertificatesSent ? StepStatus.COMPLETE : StepStatus.PENDING} />
              )
            }
            action={
              areCertificatesSent && <DocumentLink icon={<Download />}>Download certificate</DocumentLink>
            }
          />

          <TrackerRow
            title="Insurance filing updated"
            status={<StepStatusBadge status={isFilingUpdated ? StepStatus.COMPLETE : StepStatus.PENDING} />}
            action={
              isFilingUpdated && (
                <DocumentLink icon={<ExternalLink />} external>
                  Go to website
                </DocumentLink>
              )
            }
          >
            {isPaid && !isFilingUpdated && (
              <span className="text-ink-500">{carrierName} may take 24 to 48 hours to update filings…</span>
            )}
          </TrackerRow>
        </ul>
      </section>
      {/* Its own card: the outcome everything above leads to. */}
      <div className="bg-ink-50 relative overflow-hidden rounded-xl">
        {/* A separate layer, since a gradient can't transition: it fades in when coverage goes active. */}
        <div
          aria-hidden
          className={cn(
            'from-brand-200 absolute inset-0 bg-linear-to-r to-orange-100 transition-opacity duration-700 motion-reduce:transition-none',
            isActive ? 'opacity-100' : 'opacity-0'
          )}
        />
        <ul className="relative px-2 py-1">
          <TrackerRow
            title="Coverage activation"
            isBold
            status={<StepStatusBadge status={isActive ? StepStatus.ACTIVE : StepStatus.PENDING} />}
          >
            {isPaid && effectiveAt && (
              <span className="text-ink-500">
                Effective date {effectiveAt.toLocaleDateString('en-US', LONG_DATE)} at {effectiveTime}
                {!isActive && (
                  <>
                    {' '}
                    ·{' '}
                    <span className="text-foreground font-medium tabular-nums">
                      {countdown(effectiveAt, now)}
                    </span>
                  </>
                )}
              </span>
            )}
          </TrackerRow>
        </ul>
      </div>
    </div>
  )
}
